import React, { Component } from 'react';
import { Row, Col, Card, Button, Form, Table, Badge } from 'react-bootstrap';
import { withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import SideNavbar from "../components/navbars/SideNavbar";
import NavbarOne from "../components/navbars/NavbarOne";
import Footer from "../components/footer/Footer";
import * as Cookies from "es-cookie";
import { isBrowser } from "react-device-detect";
import CardLessDataTable from "../components/datatables/CardLessDataTableWithSearch";
import DateBlock from "../components/home_bck/DateBlock";
import { scopeBranches, onBranchScopeChange } from "../../utils/branchScope";

// ─── GraphQL Queries ────────────────────────────────────────────────────
// Dedicated fee-data service for the ledger — returns collections already
// aggregated by fee_type, scoped by institute, branch ids and date range.
const GET_LEDGER_FEE_DATA = gql`
    query getLedgerFeeData(
        $institute_id: Int
        $branch_ids: [Int]
        $from_date: String
        $to_date: String
    ) {
        getLedgerFeeData(
            institute_id: $institute_id
            branch_ids: $branch_ids
            from_date: $from_date
            to_date: $to_date
        ) {
            fee_type
            payment_mode
            term_amount
            internal_amount
        }
    }
`;

const GET_BRANCHES = gql`
    query getBranches($institution_id: Int!) {
        getBranches(institution_id: $institution_id) {
            id
            branch_name
        }
    }
`;

const GET_EXPENSES = gql`
    query ($filter: ExpenseFilterInput!) {
        getExpenses(filter: $filter) {
            id
            expense_date
            final_amount
            payment_status
            head_name
            category_name
            payment_mode
            description
        }
    }
`;

const GET_ORGANISATION_PROFILE = gql`
    query($institution_id: Int!) {
        getOrganisationProfile(institution_id: $institution_id) {
            id
            organisation_name
            logo
            complete_address
        }
    }
`;

class AccountsLedger extends Component {
  constructor(props) {
    super(props);
    this.state = {
      entries: [],
      summary: { totalCredit: 0, totalTermFee: 0, totalDebit: 0, netBalance: 0, totalEntries: 0 },
      branches: [],
      filters: {
        startDate: moment().format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        transactionType: 'ALL', // 'ALL' | 'FEE' | 'EXPENSE'
      },
      loading: false,
      currentPage: 1,
      pageSize: 25,
      totalCount: 0,
      orgProfile: {},
    };
  }

  menuToggler = () => {
    const toggled = Cookies.get("toggle");
    if (toggled === "wrapper") {
      Cookies.set("toggle", "wrapper sidebar-enable");
    } else {
      Cookies.set("toggle", "wrapper");
    }
    this.forceUpdate();
  };

  componentDidMount() {
    this.fetchBranches();
    this.fetchOrgProfile();
    // Branch is chosen once in the header (NavbarOne). Re-scope and refetch when
    // that selection changes instead of offering a branch filter on this page.
    this.offBranchScope = onBranchScopeChange(() => this.fetchBranches());
  }

  componentWillUnmount() {
    if (this.offBranchScope) this.offBranchScope();
  }

  // Load the branches this ledger covers: the header's selection, narrowed to
  // the branches the user is authorized for.
  fetchBranches = async () => {
    const instId = parseInt(Cookies.get("institutionid") || Cookies.get("institute_id") || "0");
    try {
      const { data } = await this.props.client.query({
        query: GET_BRANCHES,
        variables: { institution_id: instId },
        fetchPolicy: 'network-only',
      });
      const branches = scopeBranches(data.getBranches || []);
      this.setState({ branches, currentPage: 1 }, this.fetchLedger);
    } catch (err) {
      console.error("Error fetching branches:", err);
      this.fetchLedger();
    }
  }

  fetchLedger = async () => {
    this.setState({ loading: true });
    const { filters, branches } = this.state;
    const instId = parseInt(Cookies.get("institutionid") || Cookies.get("institute_id") || "0");

    // Every branch in the header's current scope.
    const branchIds = branches.map(b => parseInt(b.id));

    try {
      // Parallel fetch: fee collections + expenses
      const [feeResult, expenseResult] = await Promise.all([
        // Skip fee query if filtering to EXPENSE only
        filters.transactionType !== 'EXPENSE'
          ? this.props.client.query({
              query: GET_LEDGER_FEE_DATA,
              variables: {
                institute_id: instId,
                branch_ids: branchIds.length ? branchIds : null,
                from_date: filters.startDate,
                to_date: filters.endDate,
              },
              fetchPolicy: 'network-only',
            })
          : Promise.resolve({ data: { getLedgerFeeData: [] } }),

        // Skip expense query if filtering to FEE only
        filters.transactionType !== 'FEE'
          ? this.props.client.query({
              query: GET_EXPENSES,
              variables: {
                filter: {
                  institute_id: instId,
                  branch_ids: branchIds.length ? branchIds : null,
                  from_date: filters.startDate,
                  to_date: filters.endDate,
                },
              },
              fetchPolicy: 'network-only',
            })
          : Promise.resolve({ data: { getExpenses: [] } }),
      ]);

      // Transform fee collections → CREDIT entries
      // (already aggregated by fee_type server-side; consolidate is a no-op safeguard)
      const feeRows = feeResult.data.getLedgerFeeData || [];
      const feeEntries = this.consolidateFeeEntries(feeRows);

      // Transform expenses → DEBIT entries (only approved/Paid)
      const expenseRows = (expenseResult.data.getExpenses || [])
        .filter(e => e.payment_status === 'Paid');
      const expenseEntries = this.consolidateExpenseEntries(expenseRows);

      // Merge
      const allEntries = [...feeEntries, ...expenseEntries];
      allEntries.sort((a, b) => b.amount - a.amount);

      // Credit total EXCLUDES the term-fee portion, matching each row's Credit column
      // (amount − termFee). Term fee is summed separately in totalTermFee.
      const totalTermFee = feeEntries.reduce((s, e) => s + (e.termFee || 0), 0);
      const totalCredit = feeEntries.reduce((s, e) => s + (e.amount - (e.termFee || 0)), 0);
      const totalDebit = expenseEntries.reduce((s, e) => s + e.amount, 0);

      this.setState({
        entries: allEntries,
        summary: {
          totalCredit,
          totalTermFee,
          totalDebit,
          // Net counts ALL money in (credit + term fee) minus expenses; only the Credit
          // column display excludes term fee.
          netBalance: (totalCredit + totalTermFee) - totalDebit,
          totalEntries: allEntries.length,
        },
        totalCount: allEntries.length,
        loading: false,
      });
    } catch (err) {
      console.error("Error fetching accounts ledger:", err);
      this.setState({ loading: false });
    }
  }

  // Fetch organisation profile for print/PDF header
  fetchOrgProfile = async () => {
    const instId = parseInt(Cookies.get("institutionid") || Cookies.get("institute_id") || "0");
    try {
      const { data } = await this.props.client.query({
        query: GET_ORGANISATION_PROFILE,
        variables: { institution_id: instId },
        fetchPolicy: 'network-only',
      });
      this.setState({ orgProfile: data.getOrganisationProfile || {} });
    } catch (err) {
      console.error("Error fetching org profile:", err);
    }
  }

  // Group fee collections by fee_type + payment_mode → one CREDIT row per
  // (fee type, payment mode) so e.g. Term Fee splits into UPI / Bank / Cash
  consolidateFeeEntries = (feeRows) => {
    const groupMap = {};
    feeRows.forEach(row => {
      // "Tuition Fee" is shown as "Term Fee" in the ledger (matches Payments)
      const rawType = row.fee_type || 'Other';
      const feeType = /^tuition\s*fee$/i.test(rawType) ? 'Term Fee' : rawType;
      const mode = row.payment_mode || 'Other';
      const key = `${feeType}_${mode}`;
      if (!groupMap[key]) {
        groupMap[key] = {
          type: 'CREDIT',
          source: 'FEE',
          category: 'Fee Collection',
          subCategory: feeType,
          paymentMode: mode,
          amount: 0,
          termFee: 0,   // SUM(internal_amount) — the per-term "Term Fee" portion
        };
      }
      groupMap[key].amount += (row.term_amount || 0);
      groupMap[key].termFee += (row.internal_amount || 0);
    });
    return Object.values(groupMap);
  }

  // Group expenses by head_name + category_name + payment_mode → one DEBIT row
  // per combination so each expense head splits by UPI / Bank / Cash
  consolidateExpenseEntries = (expenseRows) => {
    const groupMap = {};
    expenseRows.forEach(row => {
      const head = row.head_name || 'Other';
      const cat = row.category_name || 'General';
      const mode = row.payment_mode || 'Other';
      const key = `${head}_${cat}_${mode}`;
      if (!groupMap[key]) {
        groupMap[key] = {
          type: 'DEBIT',
          source: 'EXPENSE',
          category: head,
          subCategory: cat,
          paymentMode: mode,
          amount: 0,
          _comments: new Set(),   // distinct expense descriptions in this group
        };
      }
      groupMap[key].amount += (row.final_amount || 0);
      const note = (row.description || '').trim();
      if (note) groupMap[key]._comments.add(note);
    });
    // Flatten the distinct descriptions into a single comments string.
    return Object.values(groupMap).map(g => {
      const { _comments, ...rest } = g;
      return { ...rest, comments: Array.from(_comments).join('; ') };
    });
  }

  handleDateBlockCallback = (startDate, endDate) => {
    this.setState(prev => ({
      filters: {
        ...prev.filters,
        startDate: startDate.format("YYYY-MM-DD"),
        endDate: endDate.format("YYYY-MM-DD")
      },
      currentPage: 1
    }), this.fetchLedger);
  };

  handleFilterChange = (key, value) => {
    this.setState(prev => ({ filters: { ...prev.filters, [key]: value } }));
  }

  handleSearch = () => {
    this.setState({ currentPage: 1 }, this.fetchLedger);
  }

  handlePageChange = (dir) => {
    const { currentPage, totalCount, pageSize } = this.state;
    const total = Math.ceil(totalCount / pageSize);
    if (dir === 'next' && currentPage < total) this.setState(p => ({ currentPage: p.currentPage + 1 }), this.fetchLedger);
    if (dir === 'prev' && currentPage > 1)     this.setState(p => ({ currentPage: p.currentPage - 1 }), this.fetchLedger);
  }

  formatCurrency = (val) =>
    '₹' + parseFloat(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  getLogoDataUrl = async (logoFile) => {
    if (!logoFile) return "";
    const url = `https://support.rizee.in/files/${logoFile}`;
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) return url;
      const blob = await res.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(url);
        reader.readAsDataURL(blob);
      });
    } catch {
      return url;
    }
  };

  handlePrint = async () => {
    const { entries, filters, summary, orgProfile } = this.state;
    const dateRangeStr = `${moment(filters.startDate).format('DD MMM YYYY')} to ${moment(filters.endDate).format('DD MMM YYYY')}`;
    
    const orgName = orgProfile.organisation_name || "College ERP";
    const orgAddress = orgProfile.complete_address || "";
    const logoFile = orgProfile.logo || ""; 
    const logoUrl = await this.getLogoDataUrl(logoFile);

    const rows = entries.map((row, idx) =>
      `<tr>
        <td>${idx + 1}</td>
        <td>${row.category || '—'}</td>
        <td>${row.subCategory || '—'}</td>
        <td>${row.paymentMode || '—'}</td>
        <td style="text-align:center">${row.type === 'CREDIT' ? `&#x20B9;${parseFloat((row.amount || 0) - (row.termFee || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}</td>
        <td style="text-align:center">${row.termFee > 0 ? `&#x20B9;${parseFloat(row.termFee || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}</td>
        <td style="text-align:center">${row.type === 'DEBIT' ? `&#x20B9;${parseFloat(row.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}</td>
        <td style="white-space:normal">${row.comments ? String(row.comments).replace(/[&<>]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;'}[c])) : '—'}</td>
      </tr>`
    ).join('');

    const win = window.open('', '_blank', 'width=1200,height=800');
    win.document.write(`<html><head><title>Accounts Ledger Report</title><style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:Arial,sans-serif;padding:24px;color:#111}
        .header{position:relative;text-align:center;border-bottom:2px solid #333;padding-bottom:12px;margin-bottom:16px;min-height:60px}
        .header img{position:absolute;left:0;top:0;width:auto;height:60px;object-fit:contain}
        .org-name{font-size:20px;font-weight:700;color:#111;margin-bottom:3px}
        .org-address{font-size:11px;color:#555;line-height:1.5;margin:0 auto;max-width:500px}
        .report-title{font-size:15px;font-weight:700;margin-bottom:4px}
        .report-sub{font-size:12px;color:#555;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;font-size:11px}
        th{background:#f3f4f6;padding:7px 10px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:.5px;border-bottom:2px solid #d1d5db;white-space:nowrap}
        td{padding:6px 10px;border-bottom:1px solid #f0f0f0;white-space:nowrap}
        tr:nth-child(even) td{background:#fafafa}
        tfoot td{font-weight:700;border-top:2px solid #d1d5db;background:#f8fafc}
        @media print{body{padding:10px}}
    </style></head><body>
        <div class="header">
            ${logoUrl ? `<img src="${logoUrl}" />` : ""}
            <div>
                <div class="org-name">${orgName}</div>
                <div class="org-address">${orgAddress}</div>
            </div>
        </div>
        <div class="report-title">Accounts Ledger Report</div>
        <div class="report-sub">${dateRangeStr} &nbsp;|&nbsp; ${entries.length} records</div>
        <table>
            <thead><tr><th>#</th><th>Category</th><th>Sub Category</th><th>Payment Mode</th><th style="text-align:center">Credit</th><th style="text-align:center">Term Fee</th><th style="text-align:center">Debit</th><th>Comments</th></tr></thead>
            <tbody>${rows}</tbody>
            <tfoot>
              <tr>
                <td colspan="4" style="text-align:right">Total Income</td>
                <td style="text-align:center; color: #10b981">&#x20B9;${summary.totalCredit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                <td style="text-align:center; color: #0891b2">&#x20B9;${(summary.totalTermFee || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td colspan="4" style="text-align:right">Total Expenses</td>
                <td></td>
                <td></td>
                <td style="text-align:center; color: #ef4444">&#x20B9;${summary.totalDebit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                <td></td>
              </tr>
              <tr>
                <td colspan="4" style="text-align:right">Net Balance</td>
                <td colspan="4" style="text-align:center; color: ${summary.netBalance >= 0 ? '#10b981' : '#ef4444'}">&#x20B9;${summary.netBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              </tr>
            </tfoot>
        </table>
        <script>
            ${logoUrl ? `var img=document.querySelector('img');if(img&&!img.complete){img.onload=function(){window.print();};img.onerror=function(){window.print()};}else{window.print();}` : `window.print();`}
        </script>
    </body></html>`);
    win.document.close();
  }

  handleExcelExport = () => {
    const { entries, summary, filters } = this.state;

    const meta = [
      ['Accounts Ledger'],
      [`Period: ${moment(filters.startDate).format('DD MMM YYYY')} to ${moment(filters.endDate).format('DD MMM YYYY')}`],
      [`Generated on: ${moment().format('DD MMM YYYY hh:mm A')}`],
      [],
      [
        `Total Income: ${this.formatCurrency(summary.totalCredit)}`,
        '',
        `Total Term Fee: ${this.formatCurrency(summary.totalTermFee || 0)}`,
        '',
        `Total Expenses: ${this.formatCurrency(summary.totalDebit)}`,
        '',
        `Net Balance: ${this.formatCurrency(summary.netBalance)}`
      ],
      [],
      ['#', 'Category', 'Sub Category', 'Payment Mode', 'Credit (INR)', 'Term Fee (INR)', 'Debit (INR)', 'Comments']
    ];

    const rows = entries.map((e, i) => [
      i + 1,
      e.category || '',
      e.subCategory || '',
      e.paymentMode || '',
      e.type === 'CREDIT' ? parseFloat((e.amount || 0) - (e.termFee || 0)) : '',
      e.termFee > 0 ? parseFloat(e.termFee || 0) : '',
      e.type === 'DEBIT' ? parseFloat(e.amount || 0) : '',
      e.comments || ''
    ]);

    const ws = XLSX.utils.aoa_to_sheet([...meta, ...rows]);
    ws['!cols'] = [
      {wch:4},{wch:20},{wch:32},{wch:16},{wch:16},{wch:16},{wch:16},{wch:40}
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Accounts Ledger');
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `Accounts_Ledger_${filters.startDate}_to_${filters.endDate}.xlsx`);
  }

  // Skeleton shimmer placeholder shown while the ledger is loading
  renderTableShimmer = () => {
    const colWidths = ['8%', '24%', '34%', '17%', '17%'];
    return (
      <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #eef1f5", overflow: "hidden" }}>
        {/* Header row */}
        <div style={{ display: "flex", gap: 16, padding: "14px 16px", background: "#f8fafc", borderBottom: "1px solid #eef1f5" }}>
          {colWidths.map((w, i) => (
            <div key={`h${i}`} className="shimmer" style={{ width: w, height: 12 }} />
          ))}
        </div>
        {/* Body rows */}
        {Array.from({ length: 8 }).map((_, r) => (
          <div key={`r${r}`} style={{ display: "flex", gap: 16, padding: "16px", borderBottom: "1px solid #f4f6f9", alignItems: "center" }}>
            {colWidths.map((w, c) => (
              <div key={`c${c}`} className="shimmer" style={{ width: w, height: 14, opacity: 1 - r * 0.04 }} />
            ))}
          </div>
        ))}
      </div>
    );
  }

  render() {
    if (Cookies.get("token") == undefined && this.props.history) {
      this.props.history.push("/login");
    }

    const { entries, summary, filters, loading, currentPage, pageSize, totalCount } = this.state;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    
    // Inline styling matches the look of Fee/Expense pages
    const styles = {
      pageTitle: { fontSize: "20px", fontWeight: 700, color: "#2d3748", marginBottom: "2px" },
    };

    const statCards = [
      { label: "Total Income", value: summary.totalCredit, color: "#10b981", icon: "fal fa-arrow-circle-up", sub: "Credits (excl. term fee)" },
      { label: "Total Term Fee", value: summary.totalTermFee || 0, color: "#0891b2", icon: "fal fa-graduation-cap", sub: "Shown separately" },
      { label: "Total Expenses", value: summary.totalDebit, color: "#ef4444", icon: "fal fa-arrow-circle-down", sub: "Overall debits" },
      { label: "Net Balance", value: summary.netBalance, color: summary.netBalance >= 0 ? "#6366f1" : "#f59e0b", icon: "fal fa-balance-scale", sub: "Credit + Term Fee − Debit" },
    ];

    const btnBase = {
      border: "none",
      padding: "8px 16px",
      borderRadius: "8px",
      fontWeight: 600,
      fontSize: "13px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
      transition: "all 0.2s ease"
    };

    return (
      <div className={Cookies.get("toggle") || "wrapper"}>
        <div className="header-area">
          <NavbarOne onClick={() => this.menuToggler()} />
        </div>
        <div className="main-wrapper">
          <div
            id="sidebar"
            className={
              isBrowser === true
                ? "sidebar-menu d-none d-xl-block d-lg-block"
                : "sidebar-menu d-block d-xl-none d-lg-none"
            }
          >
            <SideNavbar onClick={() => this.menuToggler()} />
          </div>
          <div className="student-overlay" onClick={() => this.menuToggler()} />
          <div className="content-wrapper pt-0">
            <div className="container-fluid py-4 px-4 px-xl-5 accounts-ledger-page">
        {/* Style injection for printing and some row hovers */}
        <style dangerouslySetInnerHTML={{__html: `
          .ledger-table tbody tr:hover td { filter: brightness(0.96); }
          @media print {
            .no-print, .side-nav, #sidebar, .sidebar, .topbar, .navbar, nav[class*="sidebar"], div[class*="sidebar"] { display: none !important; }
            .main-content, .content-wrapper, .page-content, main { margin-left: 0 !important; padding: 0 !important; }
            .accounts-ledger-page { padding: 0 !important; }
            .accounts-ledger-page .summary-card { box-shadow: none !important; border: 1px solid #dee2e6 !important; }
            @page { size: A4 landscape; margin: 1.2cm; }
          }
          .shimmer {
            background: #e9edf2;
            background-image: linear-gradient(90deg, #e9edf2 0px, #f4f7fa 60px, #e9edf2 120px);
            background-size: 200% 100%;
            border-radius: 6px;
            animation: shimmerMove 1.3s ease-in-out infinite;
          }
          @keyframes shimmerMove {
            0% { background-position: -150px 0; }
            100% { background-position: 250px 0; }
          }
        `}} />

        {/* Single wrapper card holding header, filters, KPI cards and the table */}
        <div style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 16,
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
          padding: "28px 32px",
        }}>

        {/* Section A & B — Page Header & Filter */}
        <div className="d-flex justify-content-between align-items-center mb-4 no-print">
          <div>
            <h4 style={styles.pageTitle}><i className="fa fa-book mr-2"></i> Accounts Ledger</h4>
            <small className="text-muted">Consolidated Income & Expense Statement</small>
          </div>
          {/* No branch filter here — branch scope comes from the header (NavbarOne). */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
            <div style={{ minWidth: '280px' }}>
              <DateBlock
                applyCallback={this.handleDateBlockCallback}
                initialStart={filters.startDate}
                initialEnd={filters.endDate}
                leftMode={true}
              />
            </div>
          </div>
        </div>

        {/* Section C — Summary KPI Cards.
            4 cards: xl=3 puts all four on one row (xl=4 left Net Balance orphaned on a
            second row); lg/md=6 gives a tidy 2×2; g-4 adds breathing room between them. */}
        <Row className="g-4 mb-4">
          {statCards.map(({ label, value, color, icon, sub }) => (
            <Col xl={3} lg={6} md={6} sm={12} key={label}>
              <div style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderTop: `4px solid ${color}`,
                borderRadius: 16,
                padding: "20px 22px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                display: "flex",
                alignItems: "center",
                gap: 16,
                height: "100%"
              }}>
                <div style={{
                  background: `${color}15`,
                  color: color,
                  borderRadius: 12,
                  width: 50,
                  height: 50,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                }}>
                  <i className={icon} style={{ fontSize: 22 }} />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>{label}</div>
                  <div style={{ fontSize: 21, fontWeight: 800, color: "#1f2937", lineHeight: 1.2 }}>{this.formatCurrency(value)}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{sub}</div>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {/* Section D — Ledger Table Card */}
        <div style={{ marginTop: 32 }}>
          {/* Clean typography section header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h5 style={{ margin: 0, fontWeight: 800, color: "#1f2937", fontSize: 16, display: "flex", alignItems: "center", gap: 8 }}>
              <i className="fal fa-list-alt" style={{ color: "#6366f1" }} />
              Ledger Entries
            </h5>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ background: "#eff6ff", color: "#6366f1", borderRadius: 20, padding: "4px 14px", fontSize: 12, fontWeight: 700, border: "1px solid #c7d2fe", marginRight: 8 }}>
                {entries.length} Records Found
              </span>
              <button onClick={this.handlePrint} style={{ ...btnBase, background: "#f1f5f9", color: "#475569" }}>
                <i className="fal fa-print" /> Print / PDF
              </button>
              <button onClick={this.handleExcelExport} style={{ ...btnBase, background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "#fff" }}>
                <i className="fal fa-file-excel" /> Download Excel
              </button>
            </div>
          </div>

          <div style={{ padding: "0" }}>
            {loading ? (
              this.renderTableShimmer()
            ) : entries.length === 0 ? (
              <div className="text-center p-5 text-muted bg-white rounded shadow-sm border">
                <i className="fa fa-inbox fa-3x d-block mb-3 text-light"></i>
                <p>No transactions found for the selected period.</p>
              </div>
            ) : (
              <CardLessDataTable
                parentData={entries.map((e, idx) => ({ 
                  ...e, 
                  id: e.id || (idx + 1),
                  sno: idx + 1,
                  debit: e.type === 'DEBIT' ? e.amount : 0,
                  // Term fee is carved out of the credit so Credit + Term Fee = collected
                  credit: e.type === 'CREDIT' ? (e.amount - (e.termFee || 0)) : 0
                }))}
                particlecolumns={[
                  { dataField: "sno", text: "#", sort: true, headerStyle: { width: '70px' } },
                  { dataField: "category", text: "Category", sort: true },
                  { dataField: "subCategory", text: "Sub Category", sort: true },
                  { dataField: "paymentMode", text: "Payment Mode", sort: true, formatter: (cell) => cell ? <span style={{ background: "#eff6ff", color: "#2563eb", borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>{cell}</span> : "-" },
                  { dataField: "credit", text: "Credit (₹)", sort: true, align: "center", headerAlign: "center", formatter: (cell) => cell > 0 ? <span className="font-weight-bold text-success">{this.formatCurrency(cell)}</span> : "-" },
                  { dataField: "termFee", text: "Term Fee (₹)", sort: true, align: "center", headerAlign: "center", formatter: (cell) => cell > 0 ? <span className="font-weight-bold text-info">{this.formatCurrency(cell)}</span> : "-" },
                  { dataField: "debit", text: "Debit (₹)", sort: true, align: "center", headerAlign: "center", formatter: (cell) => cell > 0 ? <span className="font-weight-bold text-danger">{this.formatCurrency(cell)}</span> : "-" },
                  { dataField: "comments", text: "Comments", sort: false, formatter: (cell) => cell ? <span style={{ color: "#475569", fontSize: 12 }} title={cell}>{cell}</span> : "-" },
                ]}
                name="accounts_ledger_table"
                searchBar={true}
                placeholder="Search Ledger Entries..."
              />
            )}
          </div>
        </div>
        {/* /Single wrapper card */}
        </div>
      </div>
          </div>
          <Footer />
        </div>
      </div>
    );
  }
}

export default withApollo(AccountsLedger);
